import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, ArrowUpRight } from "lucide-react";

export const CTASection = () => {
  const nav = useNavigate();
  return (
    <section className="relative py-24 lg:py-32 border-t border-white/5 overflow-hidden">
      <div className="absolute inset-0 bg-tech-grid opacity-40 pointer-events-none" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full bg-cyan-neon/10 blur-[160px] pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="max-w-4xl mx-auto px-6 text-center relative z-10"
      >
        <ShieldCheck className="w-12 h-12 text-cyan-neon mx-auto mb-8" strokeWidth={1.5} />
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white leading-[0.95]">
          Every fake click is money <span className="text-cyan-neon text-glow-cyan">you'll never get back.</span>
        </h2>
        <p className="mt-6 text-lg text-slate-300 font-light max-w-xl mx-auto">
          Turn on ClickShield AI and start protecting your ad budget in under 60 seconds.
        </p>
        <button
          data-testid="cta-final-trial"
          onClick={() => nav("/signup")}
          className="group mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-cyan-neon text-[#040914] font-semibold text-lg hover:-translate-y-0.5 hover:glow-cyan transition-all"
        >
          Start 14-day free trial
          <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </button>
        <p className="mt-4 font-mono text-xs text-slate-500 tracking-wide">No credit card required</p>
      </motion.div>
    </section>
  );
};

export const Footer = () => {
  const cols = [
    {
      title: "Product",
      links: [
        { label: "Features", to: "/#features" },
        { label: "Pricing", to: "/#pricing" },
        { label: "How it works", to: "/#how" },
        { label: "Savings calculator", to: "/#calculator" },
        { label: "FAQ", to: "/#faq" },
      ],
    },
    {
      title: "Company",
      links: [
        { label: "About", to: "/about" },
        { label: "Contact", to: "/contact" },
      ],
    },
    {
      title: "Legal",
      links: [
        { label: "Privacy", to: "/privacy" },
        { label: "Terms", to: "/terms" },
      ],
    },
  ];
  return (
    <footer className="relative border-t border-white/8 bg-[#040914] pt-16 pb-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="flex items-center gap-2.5 mb-4">
              <ShieldCheck className="w-6 h-6 text-cyan-neon" />
              <span className="font-display text-lg text-white">ClickShield<span className="text-cyan-neon"> AI</span></span>
            </div>
            <p className="text-sm text-slate-500 font-light max-w-xs leading-relaxed">
              Real-time click fraud protection for Google, Meta &amp; Bing. Stop the bots. Keep your budget.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-white/10 font-mono text-[10px] tracking-wider text-slate-400">SOC2 TYPE II</span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-white/10 font-mono text-[10px] tracking-wider text-slate-400">GDPR READY</span>
            </div>
          </div>
          {cols.map((c) => (
            <div key={c.title} className="md:col-span-2">
              <h4 className="font-mono text-[11px] tracking-widest uppercase text-slate-500 mb-4">{c.title}</h4>
              <ul className="space-y-2.5">
                {c.links.map((l) => (
                  <li key={l.label}>
                    {l.to.startsWith("/#") ? (
                      <a href={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm text-slate-400 hover:text-cyan-neon transition-colors">{l.label}</a>
                    ) : (
                      <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm text-slate-400 hover:text-cyan-neon transition-colors">{l.label}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="md:col-span-2">
            <h4 className="font-mono text-[11px] tracking-widest uppercase text-slate-500 mb-4">Status</h4>
            <span className="inline-flex items-center gap-2 text-sm text-slate-300">
              <span className="w-2 h-2 rounded-full bg-[#39FF14] animate-pulse-glow" />
              All systems operational
            </span>
          </div>
        </div>
        <div className="mt-14 pt-8 border-t border-white/8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-mono text-xs text-slate-600 tracking-wide">© 2026 ClickShield AI. All rights reserved.</p>
          <p className="font-mono text-xs text-slate-600 tracking-wide">Built for advertisers who hate waste.</p>
        </div>
      </div>
    </footer>
  );
};
